import { useCallback, useEffect, useState } from "react";
import { ActionIcon, Button, Input, Modal, Title, Tooltip } from "rizzui";
import CreditBalanceCard from "@/components/ad-credits/CreditBalanceCard";
import toast from "react-hot-toast";
import { toCurrency } from "@/utils/to-currency";
import useApi from "@/hooks/useApi";
import AdCreditRepository from "@/utilities/repositories/AdCredit";
import AdCreditTransactions from "@/components/ad-credits/transactions";
import { EXeroPaymentMethod, PaymentFlow } from "@/utilities/enums/Payment";
import { useModal } from "@/app/shared/modal-views/use-modal";
import SelectPaymentMethod from "@/components/payment-method";
import { PAYMENT_CUSTOM_SIZE } from "@/config/constants";
import UploadScreenShotNotification from "@/components/pricing-plans/upload-screenshot-notification";
import CreditCardInstructions from "@/components/payment/credit-card-instructions";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { IXeroPayment } from "@/utilities/types/Payment";
import PackageInfoCard from "@/components/ad-credits/PackageInfoCard";
import { IoMdRefresh } from "react-icons/io";
import PaymentVerify from "@/components/pricing-plans/payment-verify";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";
import PricingBreakdownComponent from "@/components/pricing-breakdown/PricingBreakdown";
import { calculatePricingBreakdown } from "@/utilities/tax-constants";

const creditBalanceTooltip =
  "Your ad credits balance is calculated by the total amount of top-ups, minus the total amount of spending. It includes expiring credits.";
const creditExpiryTooltip =
  "Your expiring credits are ad credits that will be expiring within 90 days.";

interface CreditPurchaseForm {
  creditAmount: number;
}

export default function AdCreditsDashboard() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Credit price per unit
  const [creditPrice, setCreditPrice] = useState<number>(0);
  const [payment, setPayment] = useState<IXeroPayment | null>(null);
  const [isReFetchHistory, setIsRefreshHistory] = useState(false);

  const methods = useForm({
    reValidateMode: "onChange",
    mode: "all",
  });

  const creditPurchaseForm = useForm<CreditPurchaseForm>({
    defaultValues: {
      creditAmount: 100,
    },
    mode: "onChange",
  });
  const { openModal, closeModal } = useModal();
  const { request: getUserAdCredit, response: userAdCreditInfo } = useApi({
    request: AdCreditRepository.getAdCreditInfoGeneral,
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleTopUp = () => {
    // Reset form and open the modal for purchasing credits
    creditPurchaseForm.reset({ creditAmount: 100 });
    setIsModalOpen(true);
  };

  const handleRefreshHistory = (value: boolean) => {
    setIsRefreshHistory(value);
  };

  const handlePurchaseCredits = creditPurchaseForm.handleSubmit(
    async (data) => {
      const toastId = toast.loading("Creating order...");
      try {
        const paymentRes = await AdCreditRepository.createOder({
          amount: data.creditAmount,
        });
        toast.success("Create order successfully", { id: toastId });
        if (paymentRes?.data) {
          setPayment(() => paymentRes.data);
          navigate(routes.confirmPayment, {
            state: {
              paymentId: paymentRes.data.adCreditOrderId,
              paymentConfirm: paymentRes.data,
              from: PaymentFlow.AD_CREDIT,
            },
          });
        }
      } catch (error) {
        toast.error("Error create order", { id: toastId });
        console.log(error);
      }
    },
  );

  const onPayNowComplete = () => {
    setIsModalOpen(false);
  };

  const handleCompleteCreditCardPayment = () => {
    setIsModalOpen(false);
    closeModal();
  };

  const onOpenModalPaymentSelect = useCallback(
    (payment: IXeroPayment | null, initMethod?: EXeroPaymentMethod) => {
      openModal({
        view: (
          <SelectPaymentMethod
            initMethod={initMethod}
            onPayNowClick={() => {
              closeModal();
              onOpenModalPayNow(payment);
            }}
            openModalCreditCard={() => openModalCreditCard(payment)}
            onClose={() =>
              payment ? openModalPaymentInfo(payment) : closeModal()
            }
          />
        ),
        customSize: PAYMENT_CUSTOM_SIZE,
      });
    },
    [payment?.id],
  );

  const onOpenModalPayNow = useCallback(
    (payment: IXeroPayment | null) => {
      openModal({
        view: (
          <FormProvider {...methods}>
            <UploadScreenShotNotification
              payment={payment}
              onClose={closeModal}
              onBack={(initMethod?: EXeroPaymentMethod) =>
                onOpenModalPaymentSelect(payment, initMethod)
              }
              onPayNowComplete={onPayNowComplete}
            />
          </FormProvider>
        ),
        customSize: PAYMENT_CUSTOM_SIZE,
      });
    },
    [payment?.id],
  );

  const openModalCreditCard = useCallback(
    (payment: IXeroPayment | null) => {
      openModal({
        view: (
          <CreditCardInstructions
            paymentLink={payment?.onlineInvoiceUrl || ""}
            onClose={closeModal}
            onBack={(initMethod?: EXeroPaymentMethod) =>
              onOpenModalPaymentSelect(payment, initMethod)
            }
            handleClickPaymentComplete={handleCompleteCreditCardPayment}
          />
        ),
        customSize: PAYMENT_CUSTOM_SIZE,
      });
    },
    [payment?.onlineInvoiceUrl],
  );

  const openModalPaymentInfo = (payment: IXeroPayment) => {
    openModal({
      view: (
        <>
          <PaymentVerify
            paymentConfirm={payment}
            typeId={payment?.adCreditOrderId || ""}
          />
        </>
      ),
      customSize: PAYMENT_CUSTOM_SIZE,
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await getUserAdCredit();
      handleRefreshHistory(true);
      toast.success("Ad Credit refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh Ad Credit");
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      const userAdCredit = await getUserAdCredit();
      setCreditPrice(
        Number(userAdCredit?.data.adCreditData?.[0].price || 1.35),
      );
    })();
  }, [getUserAdCredit]);

  const watchedCreditAmount = creditPurchaseForm.watch("creditAmount");

  // Calculate pricing breakdown with GST
  const pricingBreakdown =
    watchedCreditAmount > 0
      ? calculatePricingBreakdown(watchedCreditAmount, creditPrice)
      : {
          subtotal: 0,
          gst: 0,
          total: 0,
          subtotalDisplay: 0,
          gstDisplay: 0,
          totalDisplay: 0,
        };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Title as="h1" className="text-2xl font-bold">
            Ad Credits
          </Title>
          <Tooltip
            size="sm"
            content={"Click to refresh"}
            placement="right"
            color="invert"
          >
            <ActionIcon
              size="sm"
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="hover:bg-gray-50"
            >
              <IoMdRefresh
                size={16}
                className={isRefreshing ? "animate-spin" : ""}
              />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {userAdCreditInfo?.data.adCreditData[0] && (
          <PackageInfoCard
            creditPerUser={userAdCreditInfo?.data.adCreditData[0]}
          />
        )}

        <CreditBalanceCard
          title="Ad Credits Balance"
          amount={userAdCreditInfo?.data?.summary?.netBalance || 0}
          tooltipContent={creditBalanceTooltip}
          onTopUp={handleTopUp}
        />
        <CreditBalanceCard
          title="Ad Credits Expiring"
          amount={userAdCreditInfo?.data.amountExpiringSoon || 0}
          tooltipContent={creditExpiryTooltip}
          onTopUp={handleTopUp}
        />
      </div>
      <AdCreditTransactions
        isReFetchHistory={isReFetchHistory}
        handleRefreshHistory={() => handleRefreshHistory(false)}
      />

      {/* Purchase Credits Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="p-8">
          <div className="mb-6">
            <Title as="h3" className="text-lg font-semibold mb-2">
              Purchase Ad Credits
            </Title>
            <p className="text-gray-500">
              Enter the amount of credits you want to purchase.
              <br />
              Your rate: {toCurrency(creditPrice)} per credit (excluding GST).
            </p>
          </div>

          <FormProvider {...creditPurchaseForm}>
            <form onSubmit={handlePurchaseCredits} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Top-up Amount
                </label>
                <Controller
                  name="creditAmount"
                  control={creditPurchaseForm.control}
                  rules={{
                    required: "Credit amount is required",
                    min: {
                      value: 0.01,
                      message: "Credit amount must be greater than 0",
                    },
                    pattern: {
                      value: /^\d*\.?\d*$/,
                      message: "Please enter a valid number",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter amount"
                        value={field.value || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                        }}
                        error={error?.message}
                      />
                    </div>
                  )}
                />
              </div>

              <PricingBreakdownComponent breakdown={pricingBreakdown} />

              <div className="pt-4 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary-dark text-white px-4"
                  disabled={
                    !watchedCreditAmount ||
                    watchedCreditAmount <= 0 ||
                    pricingBreakdown.totalDisplay <= 0
                  }
                >
                  Confirm Order
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </Modal>
    </div>
  );
}
